import type { InterpretationDataModel } from '@/entities/optimization-statistic'
import type { TFunction } from 'i18next'

import { entries, fromPairs, isNumber, map, pipe } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import { useColumnsSchemaQuery } from '@/entities/column'

import { HiddenOnContext } from '@/shared/generated'
import { formatNumber, NumberFormats } from '@/shared/tools/locale'
import { sprintf } from '@/shared/tools/utils'

const createFieldsValuesGetter = (
	schemaNames: Record<string, string>,
	t: TFunction
) =>
	pipe([
		entries,
		map(([key, val]: [string, string]) => [
			key,
			schemaNames?.[val] ?? t(val, { defaultValue: val }),
		]),
		fromPairs,
	])

export const useFieldTranslationsResolver = (
	data: InterpretationDataModel | null
) => {
	const { t } = useTranslation()
	const { data: schema } = useColumnsSchemaQuery<Record<string, string>>(
		HiddenOnContext.Products,
		{
			select: pipe([
				map(({ name, id }: { name: string; id: string }) => [
					id,
					t(name, { ns: 'fields', defaultValue: name }),
				]),
				fromPairs,
			]),
		}
	)
	const schemaFieldNames = schema || {}
	const fieldsValuesGetter = createFieldsValuesGetter(schemaFieldNames, t)
	const fieldsValues = fieldsValuesGetter(data?.fields || {})
	const dataValues = pipe(
		entries,
		map(([key, val]) => [
			key,
			isNumber(val) ? formatNumber(val, NumberFormats.Fixed) : val,
		]),
		fromPairs
	)(data?.values || {})
	const stringVaraibles = { ...dataValues, ...fieldsValues }

	return (items: string[]): string[] =>
		items.map(
			(item: string) =>
				t(item, {
					...stringVaraibles,
					defaultValue: sprintf(item, stringVaraibles),
				}) as string
		)
}
